import { useCallback, useEffect, useRef, useState } from "react";
import { convertDateToUTCRange } from "../../../lib/date-time";
import { getApiErrorMessage } from "../../../lib/result-codes";
import { getEventList } from "../api/event.api";
import {
  defaultEventFilter,
  type EventFilters,
  type EventListForAdminResponse,
  type EventStatus,
  type GetEventListForAdminRequest,
} from "../types/event.types";

export interface UseGetEventListRequest {
  search: string;
  filters: EventFilters;
  pagination: {
    page: number;
    totalCount: number;
  };
  sorting?: {
    sort_key?: string;
    sort_direction?: "asc" | "desc";
  };
}

interface UseGetEventListReturn {
  events: EventListForAdminResponse[];
  isLoading: boolean;
  isError: boolean;
  error: string | null;
  refreshEvents: () => Promise<void>;
  fetchEvent: () => Promise<void>;
  request: UseGetEventListRequest;
  haveFilter: boolean;
  setFilter: (filters: EventFilters | ((prev: EventFilters) => EventFilters)) => void;
  setPagination: (
    pagination:
      | UseGetEventListRequest["pagination"]
      | ((prev: UseGetEventListRequest["pagination"]) => UseGetEventListRequest["pagination"]),
  ) => void;
  setSorting: (sorting: UseGetEventListRequest["sorting"]) => void;
  hasInitialData: boolean;
}

const defaultRequest: UseGetEventListRequest = {
  search: "",
  filters: defaultEventFilter,
  pagination: {
    page: 1,
    totalCount: 0,
  },
};

const isEventStatus = (status: string): status is EventStatus =>
  status === "upcoming" || status === "ongoing" || status === "completed";

const toListRequest = (
  filters: EventFilters,
  pagination: { index: number; size: number },
  sorting: UseGetEventListRequest["sorting"],
): GetEventListForAdminRequest => ({
  ...(filters.title ? { title: filters.title } : {}),
  ...(filters.event_date.from
    ? { event_date_from: convertDateToUTCRange(filters.event_date.from, true) }
    : {}),
  ...(filters.event_date.to
    ? { event_date_to: convertDateToUTCRange(filters.event_date.to, false) }
    : {}),
  ...(filters.category_id ? { category_id: filters.category_id } : {}),
  ...(filters.location ? { location_name: filters.location } : {}),
  ...(filters.event_time.from ? { time_start: filters.event_time.from } : {}),
  ...(filters.event_time.to ? { time_end: filters.event_time.to } : {}),
  ...(filters.points.min !== null ? { min_points: filters.points.min } : {}),
  ...(filters.points.max !== null ? { max_points: filters.points.max } : {}),
  ...(filters.attending.min !== null ? { attending_count_start: filters.attending.min } : {}),
  ...(filters.attending.max !== null ? { attending_count_end: filters.attending.max } : {}),
  ...(filters.checkin.min !== null ? { check_in_count_start: filters.checkin.min } : {}),
  ...(filters.checkin.max !== null ? { check_in_end: filters.checkin.max } : {}),
  ...(filters.status.length ? { event_status: filters.status.filter(isEventStatus) } : {}),
  ...(sorting?.sort_key ? { sort_key: sorting.sort_key } : {}),
  ...(sorting?.sort_direction ? { sort_direction: sorting.sort_direction } : {}),
  index: pagination.index,
  size: pagination.size,
});

const getHasActiveFilters = (source: EventFilters) =>
  source.title !== "" ||
  source.event_date.from !== "" ||
  source.event_date.to !== "" ||
  source.category_id !== "" ||
  source.interest_id !== "" ||
  source.location !== "" ||
  source.event_time.from !== "" ||
  source.event_time.to !== "" ||
  source.points.min !== null ||
  source.points.max !== null ||
  source.attending.min !== null ||
  source.attending.max !== null ||
  source.checkin.min !== null ||
  source.checkin.max !== null ||
  source.status.length > 0;

export const useGetEventList = (): UseGetEventListReturn => {
  const [events, setEvents] = useState<EventListForAdminResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [request, setRequest] = useState<UseGetEventListRequest>(defaultRequest);
  const [hasInitialData, setHasInitialData] = useState(false);
  const sessionRef = useRef(0);
  const requestRef = useRef(request);
  const skipNextAutoFetchRef = useRef(false);
  const itemsPerPage = 20;
  const haveFilter = getHasActiveFilters(request.filters);

  useEffect(() => {
    requestRef.current = request;
  }, [request]);

  const fetchEventWithRequest = useCallback(async (currentRequest: UseGetEventListRequest) => {
    sessionRef.current += 1;
    const currentSession = sessionRef.current;

    try {
      const hasActiveFilters = getHasActiveFilters(currentRequest.filters);

      if (hasActiveFilters) {
        setEvents([]);
      }

      setIsLoading(true);
      setIsError(false);
      setError(null);

      const response = await getEventList(
        toListRequest(
          currentRequest.filters,
          {
            index: currentRequest.pagination.page - 1,
            size: itemsPerPage,
          },
          currentRequest.sorting,
        ),
      );

      if (currentSession !== sessionRef.current) return;

      setEvents([...(response.list || [])]);
      setRequest((prev) => ({
        ...prev,
        pagination: {
          ...prev.pagination,
          totalCount: response.paging?.total || 0,
        },
      }));
      setHasInitialData(true);
    } catch (fetchError: unknown) {
      if (currentSession !== sessionRef.current) return;

      setIsError(true);
      setError(getApiErrorMessage(fetchError, "Failed to load events"));
    } finally {
      if (currentSession === sessionRef.current) {
        setIsLoading(false);
      }
    }
  }, []);

  const fetchEvent = useCallback(async () => {
    const currentRequest = requestRef.current;
    await fetchEventWithRequest(currentRequest);
  }, [fetchEventWithRequest]);

  const refreshEvents = useCallback(async () => {
    if (haveFilter) {
      const clearedRequest: UseGetEventListRequest = {
        ...defaultRequest,
        pagination: {
          ...requestRef.current.pagination,
          page: 1,
          totalCount: 0,
        },
      };

      skipNextAutoFetchRef.current = true;
      requestRef.current = clearedRequest;
      setRequest(clearedRequest);
      setEvents([]);
      await fetchEventWithRequest(clearedRequest);
      return;
    }

    setEvents([]);
    await fetchEvent();
  }, [fetchEvent, fetchEventWithRequest, haveFilter]);

  const setFilter = useCallback(
    (filters: EventFilters | ((prev: EventFilters) => EventFilters)) => {
      setRequest((prev) => ({
        ...prev,
        filters: typeof filters === "function" ? filters(prev.filters) : filters,
        pagination: {
          ...prev.pagination,
          page: 1,
        },
      }));
    },
    [],
  );

  const setPagination = useCallback(
    (
      pagination:
        | UseGetEventListRequest["pagination"]
        | ((prev: UseGetEventListRequest["pagination"]) => UseGetEventListRequest["pagination"]),
    ) => {
      setRequest((prev) => ({
        ...prev,
        pagination: typeof pagination === "function" ? pagination(prev.pagination) : pagination,
      }));
    },
    [],
  );

  const setSorting = useCallback((sorting: UseGetEventListRequest["sorting"]) => {
    setRequest((prev) => ({
      ...prev,
      sorting,
      pagination: {
        ...prev.pagination,
        page: 1,
      },
    }));
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (skipNextAutoFetchRef.current) {
        skipNextAutoFetchRef.current = false;
        return;
      }
      fetchEvent();
    }, 500);

    return () => clearTimeout(timer);
  }, [request.filters, request.pagination.page, request.sorting, fetchEvent]);

  return {
    events,
    isLoading,
    isError,
    error,
    refreshEvents,
    fetchEvent,
    request,
    haveFilter,
    setFilter,
    setPagination,
    setSorting,
    hasInitialData,
  };
};
